import { useState, useEffect } from 'react';
import { Settings, Key, Globe, Cpu } from 'lucide-react';
import { useStore } from './store/useStore';
import { Layout } from './components/Layout';
import { ExcelUploader } from './components/ExcelUploader';
import { RadarChartComponent } from './components/RadarChart';
import { HeatMap } from './components/HeatMap';
import { DiagnosisPanel } from './components/DiagnosisPanel';
import { ExportPanel } from './components/ExportPanel';
import { StudentDashboard } from './components/StudentDashboard';

function DashboardPage() {
  const [dashboardTab, setDashboardTab] = useState<'class' | 'student'>('class');

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-800">数据看板</h2>
        <div className="flex bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setDashboardTab('class')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              dashboardTab === 'class'
                ? 'bg-white text-blue-600 shadow'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            班级看板
          </button>
          <button
            onClick={() => setDashboardTab('student')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              dashboardTab === 'student'
                ? 'bg-white text-blue-600 shadow'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            学生看板
          </button>
        </div>
      </div>

      {dashboardTab === 'class' ? (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <RadarChartComponent />
            <HeatMap />
          </div>
          <DiagnosisPanel />
        </div>
      ) : (
        <StudentDashboard />
      )}
    </div>
  );
}

function ApiKeySettings({ onClose }: { onClose: () => void }) {
  const { apiKey, apiUrl, apiModel, setApiKey, setApiUrl, setApiModel } = useStore();
  const [inputKey, setInputKey] = useState(apiKey || '');
  const [inputUrl, setInputUrl] = useState(apiUrl || 'https://api.siliconflow.cn/v1');
  const [inputModel, setInputModel] = useState(apiModel || 'deepseek-ai/DeepSeek-V3.2');

  const handleSave = () => {
    setApiKey(inputKey.trim());
    setApiUrl(inputUrl.trim());
    setApiModel(inputModel.trim());
    localStorage.setItem('api_key', inputKey.trim());
    localStorage.setItem('api_url', inputUrl.trim());
    localStorage.setItem('api_model', inputModel.trim());
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 w-full max-w-md">
        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <Key className="w-5 h-5" />
          AI 配置设置
        </h3>

        <div className="space-y-4">
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
              <Key className="w-4 h-4" />
              API Key
            </label>
            <input
              type="password"
              value={inputKey}
              onChange={(e) => setInputKey(e.target.value)}
              placeholder="sk-xxxxxxxxxxxxxxxx"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
              <Globe className="w-4 h-4" />
              API URL
            </label>
            <input
              type="text"
              value={inputUrl}
              onChange={(e) => setInputUrl(e.target.value)}
              placeholder="https://api.siliconflow.cn/v1"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">例如: https://api.siliconflow.cn/v1 或 https://api.deepseek.com/v1</p>
          </div>

          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
              <Cpu className="w-4 h-4" />
              Model
            </label>
            <input
              type="text"
              value={inputModel}
              onChange={(e) => setInputModel(e.target.value)}
              placeholder="deepseek-ai/DeepSeek-V3.2"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">例如: deepseek-ai/DeepSeek-V3.2, gpt-4o, claude-3-sonnet</p>
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <button
            onClick={handleSave}
            className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700"
          >
            保存
          </button>
          <button
            onClick={onClose}
            className="flex-1 bg-gray-200 text-gray-700 py-2 rounded-lg hover:bg-gray-300"
          >
            取消
          </button>
        </div>
      </div>
    </div>
  );
}

function App() {
  const [currentPage, setCurrentPage] = useState('upload');
  const [showSettings, setShowSettings] = useState(false);
  const { setApiKey, setApiUrl, setApiModel } = useStore();

  useEffect(() => {
    const savedKey = localStorage.getItem('api_key');
    const savedUrl = localStorage.getItem('api_url');
    const savedModel = localStorage.getItem('api_model');

    if (savedKey) setApiKey(savedKey);
    if (savedUrl) setApiUrl(savedUrl);
    if (savedModel) setApiModel(savedModel);
  }, [setApiKey, setApiUrl, setApiModel]);

  const handleNavigate = (page: string) => {
    setCurrentPage(page);
  };

  const renderPage = () => {
    switch (currentPage) {
      case 'upload':
        return <ExcelUploader />;
      case 'dashboard':
        return <DashboardPage />;
      case 'export':
        return <ExportPanel />;
      default:
        return <ExcelUploader />;
    }
  };

  return (
    <>
      <Layout currentPage={currentPage} onNavigate={handleNavigate}>
        {renderPage()}
      </Layout>
      <button
        onClick={() => setShowSettings(true)}
        className="fixed bottom-4 right-4 p-3 bg-white rounded-full shadow-lg hover:bg-gray-50"
        title="设置"
      >
        <Settings className="w-6 h-6 text-gray-600" />
      </button>
      {showSettings && <ApiKeySettings onClose={() => setShowSettings(false)} />}
    </>
  );
}

export default App;